import styled, { css } from 'styled-components';

/* ---------- tokens ---------- */

const accent = 'var(--btn-primary)';
const accentSoft = 'rgba(0, 180, 216, 0.14)';
const borderSoft = 'rgba(255, 255, 255, 0.06)';
const borderStrong = 'rgba(255, 255, 255, 0.14)';
const panel = '#24344D';
const panelDeep = 'rgba(11, 19, 32, 0.55)';
const mono = "'JetBrains Mono', 'Fira Code', 'Consolas', monospace";

const techCorners = css`
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
    opacity: 0.9;
  }
  &::before {
    top: -1px;
    left: -1px;
    border-top-width: 1px;
    border-left-width: 1px;
  }
  &::after {
    bottom: -1px;
    right: -1px;
    border-bottom-width: 1px;
    border-right-width: 1px;
  }
`;

/* ---------- contenedor principal ---------- */

export const CompetitorsContainer = styled.div`
  position: relative;
  padding: 0.5rem 0 2rem;

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
    background-size: 32px 32px;
    mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.6), transparent 75%);
  }

  > * { position: relative; z-index: 1; }
`;

/* ---------- header del módulo ---------- */

export const CompetitorsHeader = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
  padding: 1.1rem 1.4rem;
  background:
    linear-gradient(135deg, rgba(0, 180, 216, 0.06), transparent 55%),
    rgba(36, 52, 77, 0.7);
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  backdrop-filter: blur(6px);
  ${techCorners}
`;

export const CompetitorsTitle = styled.h1`
  margin: 0 0 0.4rem;
  color: var(--text-primary);
  font-size: 1.45rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${accent};
    box-shadow: 0 0 0 3px ${accentSoft};
  }

  &::after {
    content: 'OPS://COMP-MAP/01';
    margin-left: auto;
    font-family: ${mono};
    font-size: 0.65rem;
    letter-spacing: 0.18em;
    color: ${accent};
    padding: 3px 8px;
    border: 1px solid ${borderStrong};
    border-radius: 4px;
  }
`;

export const CompetitorsSubtitle = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.92rem;
`;

/* ---------- grid ---------- */

export const CompetitorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 1.25rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(6, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const GridItem = styled.div`
  grid-column: span ${({ span }) => span || 12};
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

/* ---------- search & filters ---------- */

export const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.25rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SearchInput = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.5rem 0.35rem 0.85rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:focus-within {
    border-color: rgba(0, 180, 216, 0.45);
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.1);
  }

  &::before {
    content: '>';
    color: ${accent};
    font-family: ${mono};
    font-size: 0.85rem;
    margin-right: 0.1rem;
  }

  > svg {
    color: var(--text-disabled);
    font-size: 0.85rem;
  }

  input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: var(--text-primary);
    font-size: 0.92rem;
    padding: 0.45rem 0.25rem;
    font-family: inherit;

    &::placeholder {
      color: var(--text-disabled);
    }
  }
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.55rem 0.9rem;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.82rem;
  font-family: inherit;
  letter-spacing: 0.04em;
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease;

  svg { font-size: 0.85rem; }

  &:hover:not(:disabled) {
    color: var(--text-primary);
    border-color: ${borderStrong};
    background: rgba(255, 255, 255, 0.03);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const FilterButton = styled.button`${buttonBase}`;

export const AddButton = styled.button`
  ${buttonBase}
  color: ${accent};
  border-color: rgba(0, 180, 216, 0.35);
  background: rgba(0, 180, 216, 0.06);

  &:hover:not(:disabled) {
    color: var(--text-primary);
    background: rgba(0, 180, 216, 0.14);
    border-color: rgba(0, 180, 216, 0.55);
  }
`;

/* ---------- tabs ---------- */

export const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-bottom: 1px solid ${borderSoft};
  margin-bottom: 1.25rem;
  overflow-x: auto;
  padding-bottom: 0;

  &::-webkit-scrollbar { height: 4px; }
`;

export const Tab = styled.button`
  position: relative;
  padding: 0.7rem 1rem 0.85rem;
  background: none;
  border: none;
  color: ${({ active }) => (active ? 'var(--text-primary)' : 'var(--text-disabled)')};
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.18s ease;

  .tab-index {
    font-family: ${mono};
    font-size: 0.65rem;
    letter-spacing: 0.18em;
    color: ${({ active }) => (active ? accent : 'var(--text-disabled)')};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${accent};
    transform: scaleX(${({ active }) => (active ? 1 : 0)});
    transform-origin: left;
    transition: transform 0.18s ease;
  }

  &:hover { color: var(--text-primary); }
`;

/* ---------- competitor card ---------- */

export const CompetitorCard = styled.div`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.015), transparent 35%),
    ${panel};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  margin-bottom: 0;
  overflow: hidden;
  transition: border-color 0.18s ease;

  &:hover {
    border-color: ${borderStrong};
  }

  ${techCorners}
`;

export const CompetitorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid ${borderSoft};

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;

export const CompetitorAvatar = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--color-tertiary);
  border: 1px solid ${borderStrong};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border: 1px solid ${({ borderColor }) => borderColor || accent};
    z-index: 2;
  }
  &::before {
    top: 4px; left: 4px;
    border-right: none; border-bottom: none;
  }
  &::after {
    bottom: 4px; right: 4px;
    border-left: none; border-top: none;
  }

  @media (max-width: 768px) { width: 56px; height: 56px; }
  @media (max-width: 480px) { width: 48px; height: 48px; }
`;

export const CompetitorImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.92) contrast(1.02);
`;

export const CompetitorInfo = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 480px) {
    width: calc(100% - 60px);
  }
`;

export const CompetitorName = styled.h3`
  margin: 0 0 0.25rem;
  color: var(--text-primary);
  font-size: 1.1rem;
  letter-spacing: -0.005em;
`;

export const CompetitorParty = styled.div`
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin-bottom: 0.55rem;
`;

export const CompetitorStats = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

export const StatBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.18rem 0.55rem;
  border: 1px solid ${({ color }) =>
    color ? 'rgba(255, 255, 255, 0.14)' : borderSoft};
  border-radius: 3px;
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: ${({ color }) => color || 'var(--text-secondary)'};
  background: rgba(11, 19, 32, 0.45);

  svg { font-size: 0.75rem; }
`;

export const CompetitorActions = styled.div`
  display: flex;
  gap: 0.4rem;

  @media (max-width: 480px) {
    margin-top: 0.75rem;
    width: 100%;
    justify-content: flex-end;
  }
`;

export const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${borderSoft};
  background: transparent;
  color: var(--text-disabled);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease;

  &:hover {
    color: ${accent};
    border-color: rgba(0, 180, 216, 0.4);
    background: rgba(0, 180, 216, 0.06);
  }
`;

export const CompetitorBody = styled.div`
  padding: 1.25rem;
`;

export const CompetitorSection = styled.div`
  margin-bottom: 1.25rem;
  &:last-child { margin-bottom: 0; }
`;

export const SectionTitle = styled.h4`
  margin: 0 0 0.85rem;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${borderSoft};

  &::before {
    content: '';
    display: inline-block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${accent};
    box-shadow: 0 0 0 2px ${accentSoft};
  }
`;

/* ---------- fortalezas / debilidades ---------- */

export const StrengthWeaknessList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StrengthWeaknessCard = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${({ color }) => color || accent};
  border-radius: 4px;
  padding: 0.85rem 0.95rem;
`;

export const CardTitle = styled.h5`
  margin: 0 0 0.6rem;
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ color }) => color || 'var(--text-secondary)'};
  display: flex;
  align-items: center;
  gap: 0.4rem;

  svg { font-size: 0.8rem; }
`;

export const ItemsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--text-secondary);

  li {
    position: relative;
    padding-left: 0.95rem;
    margin-bottom: 0.4rem;
    font-size: 0.85rem;
    line-height: 1.45;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.55em;
      width: 5px;
      height: 1px;
      background: ${accent};
      opacity: 0.7;
    }

    &:last-child { margin-bottom: 0; }
  }
`;

/* ---------- progress bars ---------- */

export const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.4rem;
`;

export const Progress = styled.div`
  height: 100%;
  background: ${({ color }) => color || accent};
  width: ${({ value }) => value}%;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
`;

/* ---------- comparison table ---------- */

export const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const TableHead = styled.thead`
  background: rgba(255, 255, 255, 0.025);
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid ${borderSoft};
  &:last-child { border-bottom: none; }
`;

export const TableHeader = styled.th`
  padding: 0.6rem 0.9rem;
  text-align: left;
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  font-weight: 500;
`;

export const TableCell = styled.td`
  padding: 0.65rem 0.9rem;
  color: var(--text-secondary);
  font-size: 0.88rem;
  vertical-align: top;
`;
